"use client";

import PlaceholderImage from "@/app/assets/images/img/placeholder.png";
import PlusIcon from "@/app/assets/images/svg/plus.svg";
import TickIcon from "@/app/assets/images/svg/tick.svg";
import mapProductOptions from "@/hooks/mapProductOptions";
import useStoreKeys from "@/hooks/useStoreKeys";
import { ProductData } from "@/lib/5874/types";
import { useCartContext } from "@/providers/CartContextProvider";
import { Builder } from "@builder.io/sdk";
import clsx from "clsx";
import Link from "next/link";
import React, {
	useContext,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
} from "react";
import SwiperType from "swiper";
import { Navigation, Scrollbar } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import Pricing from "../ProductCard/Pricing/Pricing";
import ProductCard from "../ProductCard/ProductCard";
import SwiperNavigationButtons from "../SwiperNavigationButton/SwiperNavigationButton";
import Button from "../UI/Button/Button";
import IconOnlyButton from "../UI/Button/IconOnlyButton/IconOnlyButton";
import CompressedImage from "../UI/CompressedImage/CompressedImage";
import ProductSliderSkeleton, {
	ProductSliderSkeletonSmall,
} from "../UI/Skeleton/ProductSliderSkeleton";
import styles from "./ProductRecommendationCarousel.module.scss";

import { handleAddToCart } from "@/components/PDP/ProductForm/_actions/addToCart";
import {
	getCartDatalayerDetails,
	getItemListData,
	useDataLayerContext,
} from "@/datalayer/DataLayerContextProvider";
import useBeaconClientArgs from "@/hooks/useBeaconClientArgs";
import {
	AdditionalArgs,
	CommonArgs,
	PlacementType,
	beaconTrackProductClick,
	beaconTrackProductsImpression,
	beaconTrackProductsRender,
	genericSendBeaconEvent,
	getNewlyVisibleProduct,
	getVisibleProducts,
} from "@/lib/searchspring/tracker";
import { BigCommerceStoreContext } from "@/providers/BigCommerceStoreProvider";
// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/scrollbar";

export interface ProductRecommendationCarouselProps {
	title: string;
	searchSpringId: string;
	useProductCards?: boolean;
	products?: ProductData[];
	placementProductId: string;
}

const ProductRecommendationCarousel: React.FC<
	ProductRecommendationCarouselProps
> = ({
	products,
	title,
	useProductCards,
	searchSpringId,
	placementProductId,
}) => {
	const { store, lang } = useStoreKeys();
	const [productAddedToCart, setProductAddedToCart] = useState(0);
	const { setCart, setIsModifyingCart, isModifyingCart, cart } =
		useCartContext();
	const carouselId = useId();
	const { currencyFormattingRules } = useContext(BigCommerceStoreContext);

	// Datalayer events
	const { viewItemList } = useDataLayerContext();
	const { addToCart } = useDataLayerContext();
	const cartDetails = getCartDatalayerDetails(cart);
	// TODO fix this - referenced outside the provider
	// const t = useI18n();

	// Datalayer
	useEffect(() => {
		if (products?.length) {
			viewItemList(
				products.filter((p) => !p.isNotPurchasable),
				searchSpringId,
				currencyFormattingRules.currency ?? "",
				title,
			);
		}
	}, [products]);

	// Beacon Data
	const availableProducts = useMemo(
		() => products?.filter((p) => !p.isNotPurchasable) ?? [],
		[products],
	);

	const [swiper, setSwiper] = useState<SwiperType | undefined>();
	const { siteId, pageLoadId, ssCookieData } = useBeaconClientArgs();
	const previousVisibleSlidesRef = useRef<number[]>([]);
	const [inViewport, setInViewport] = useState(false);
	const seedData = availableProducts.map((p) => String(p.id));

	// Beacon payload
	const payload = {
		products: availableProducts,
		ssCookieData,
		siteId,
		pageLoadId,
		tag: searchSpringId,
		placement: "product-page" as PlacementType,
		skus: seedData,
		placementProductId: String(placementProductId) ?? "",
	};

	// Beacon click product event
	const profileClickData: CommonArgs & AdditionalArgs = useMemo(
		() => ({
			siteId,
			pageLoadId,
			ssCookieData,
			tag: searchSpringId,
			placement: "product-page" as PlacementType,
			skus: seedData,
			placementProductId: String(placementProductId) ?? "",
		}),
		[],
	);

	const productClickPayload = (product: ProductData) => {
		return {
			siteId,
			pageLoadId,
			ssCookieData,
			tag: searchSpringId,
			placement: "product-page" as PlacementType,
			product,
			skus: [String(product.id)],
			placementProductId,
			href: product.path,
		};
	};

	// Experimental
	const handleSlideChange = (swiper: SwiperType | undefined) => {
		if (!swiper) return;
		const slidesPerView = Number(swiper.params.slidesPerView);

		const visibleSlides = swiper.slides
			.map((slide, index) => {
				const isVisible =
					index >= swiper.activeIndex &&
					index < Number(swiper.activeIndex) + slidesPerView;
				return isVisible ? index : null;
			})
			.filter((index): index is number => index !== null);

		const previousVisibleSlides = previousVisibleSlidesRef.current;
		const newlyVisibleSlides = visibleSlides.filter(
			(index) => !previousVisibleSlides.includes(index),
		);

		if (newlyVisibleSlides?.length > 0) {
			const newProducts = getNewlyVisibleProduct(products, newlyVisibleSlides);

			if (newProducts?.length) {
				const events = beaconTrackProductsImpression({
					...payload,
					products: newProducts,
				});
				genericSendBeaconEvent(events, store);
			}
		}
		previousVisibleSlidesRef.current = visibleSlides;
	};

	useEffect(() => {
		if (swiper?.el && availableProducts) {
			swiper.update();
			if (Builder.isEditing) return;

			const observer = new IntersectionObserver((entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					setInViewport(true);
				}
			});
			observer.observe(swiper.el);
		}
	}, [swiper, availableProducts]);

	useEffect(() => {
		if (
			availableProducts?.length &&
			swiper &&
			swiper.slides &&
			!Builder.isEditing
		) {
			const renderEvents = beaconTrackProductsRender(payload);
			genericSendBeaconEvent(renderEvents, store);
		}
	}, [availableProducts]);

	useEffect(() => {
		if (swiper && inViewport && !Builder.isEditing && availableProducts) {
			const visibleProducts = getVisibleProducts(availableProducts, swiper);

			if (visibleProducts?.length && products !== undefined) {
				const impressionEvents = beaconTrackProductsImpression({
					...payload,
					products: getVisibleProducts(availableProducts, swiper) ?? products,
				});

				genericSendBeaconEvent(impressionEvents, store);
			}
		}
	}, [inViewport, availableProducts, swiper?.slides]);

	// HANDLER

	const addToCartHandler = async (id: number) => {
		setIsModifyingCart(true);
		const res = await handleAddToCart([{ id: id, quantity: 1 }]);

		if (res.status === "success") {
			setCart(res.data);
			setProductAddedToCart(id);
		}

		setIsModifyingCart(false);

		setTimeout(() => setProductAddedToCart(0), 1500);
	};

	const skeletonContent = useProductCards
		? ProductSliderSkeleton
		: ProductSliderSkeletonSmall;

	const content = (
		<>
			<h2 className={styles.title}>{title}</h2>
			<Swiper
				modules={[Scrollbar, Navigation]}
				scrollbar={true}
				onSwiper={setSwiper}
				onSlideChange={(swiper) => handleSlideChange(swiper)}
				noSwiping
				noSwipingSelector='a'
				preventClicks={false}
				preventClicksPropagation={false}
				watchSlidesProgress={true}
				navigation={{
					enabled: true,
					nextEl: `[data-button-ref="${carouselId}-button-next"]`,
					prevEl: `[data-button-ref="${carouselId}-button-previous"]`,
				}}
				spaceBetween={16}
				breakpoints={{
					1000: {
						slidesPerView: useProductCards ? 4 : 2,
					},
					700: {
						slidesPerView: useProductCards ? 2 : 2,
					},
					551: {
						slidesPerView: 1,
					},
				}}
			>
				{!products || products.length === 0
					? skeletonContent
					: availableProducts.map((product, index: number) => {
							const thumbnail =
								product.media?.images?.find((image) => image.isThumbnail) ||
								product.media?.images?.[0];

							const productSpecifications = mapProductOptions([product]);

							return (
								<SwiperSlide
									key={`${product.id}-${index}`}
									onClick={() => {
										const payload = productClickPayload(product);
										beaconTrackProductClick(payload, store);
									}}
									data-index={index}
									data-tracked={false}
									className={styles.slide}
								>
									{useProductCards ? (
										<ProductCard
											product={product}
											showSimpleView={true}
											className={styles.productCardOverrides}
											currencyCode={currencyFormattingRules.currency}
											itemListId={searchSpringId}
											itemListName={title}
											isInRecommendationCarousel={true}
										/>
									) : (
										<article key={product.id} className={clsx([styles.card])}>
											<div className={styles.inner}>
												<CompressedImage
													src={`${
														thumbnail?.url?.thumbnail || PlaceholderImage.src
													}?format=webp`}
													height={80}
													width={80}
													altText={product.name}
													containerClass={styles.image}
													imgLink={`/${store}/${lang}${product.path}`}
													withNextImage={false}
												/>
												<div className={styles.details}>
													{product.brand && (
														<Link
															className={styles.brand}
															href={`/${store}/${lang}${product.brand.path}`}
														>
															{product.brand.name}
														</Link>
													)}
													<Link
														href={`/${store}/${lang}${product.path}`}
														className={styles.name}
													>
														{product.name}
													</Link>
													{productSpecifications &&
														productSpecifications.length > 0 && (
															<div className={styles.specs}>
																{productSpecifications?.map((spec) => (
																	<div key={spec.label} className={styles.spec}>
																		<span className={styles.label}>
																			{spec.label}:
																		</span>
																		<span className={styles.value}>
																			{spec.options[0].label}
																		</span>
																	</div>
																))}
															</div>
														)}
													<Pricing
														className={styles.price}
														prodData={product}
													/>
												</div>
												<div className={styles.ctaDesktop}>
													<IconOnlyButton
														variant='tertiary'
														icon={
															productAddedToCart === product.id ? (
																<TickIcon />
															) : (
																<PlusIcon />
															)
														}
														type='button'
														onClick={(e) => {
															e.stopPropagation();
															addToCartHandler(product.id);
															const datalayerProduct = {
																product: product,
																quantity: 1,
																variantId: product.id,
															};
															addToCart({
																products: [datalayerProduct],
																itemListId: getItemListData(product).itemListId,
																itemListName:
																	getItemListData(product).itemListName,
																cart: cartDetails,
															});
														}}
														isLoading={isModifyingCart}
													/>
												</div>
											</div>
											<div className={styles.ctaMobile}>
												<Button
													as='button'
													variant='secondary'
													onClick={() => addToCartHandler(product.id)}
													isLoading={isModifyingCart}
												>
													Add to cart
												</Button>
											</div>
										</article>
									)}
								</SwiperSlide>
							);
					  })}

				<SwiperNavigationButtons
					buttonIdentifier={carouselId}
					clickData={profileClickData}
					store={store}
				/>
			</Swiper>
		</>
	);

	return (
		<section
			className={clsx([
				styles.productRecommendations,
				useProductCards ? styles.fullSize : "",
			])}
		>
			{useProductCards ? (
				<div className={styles.inner}>{content}</div>
			) : (
				content
			)}
		</section>
	);
};

export default ProductRecommendationCarousel;
